import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, DialogConfirm } from 'components'
import { toast } from 'react-toastify'
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material'
import { cloneDeep } from 'lodash'
import { useSelector } from 'react-redux'
import { submitExtraAutoAnswer } from 'services/apiKYP'

import styles from './styles'

const initialValues = {
  role: '',
  developed_activities: '',
  reports_to: '',
  organization: '',
  currently_at_this_job: false,
  end_of_contract: null,
  start_working_hours: null,
  end_working_hours: null,
  answer_type: 'pep',
}

const initialErrors = {
  role: false,
  developed_activities: false,
  reports_to: false,
  organization: false,
  end_of_contract: false,
  start_working_hours: false,
  end_working_hours: false,
}

function PepForm(props) {
  const { questionData } = props

  const token = useSelector((state) => state.auth.access_token)
  const classes = styles()

  const [formState, setFormState] = useState(initialValues)
  const [formErrors, setFormErrors] = useState(initialErrors)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const handleValidate = () => {
    const keysToExclude = [
      'start_working_hours',
      'end_working_hours',
      'end_of_contract',
    ]
    const errors = cloneDeep(initialErrors)
    Object.keys(errors).forEach((key) => {
      if (formState.currently_at_this_job) {
        if (key === 'start_working_hours' || key === 'end_working_hours') {
          errors[key] = formState[key] === ''
          return
        }
      } else if (key === 'end_of_contract') {
        errors[key] = formState[key] === ''
        return
      }
      if (!keysToExclude.includes(key)) errors[key] = formState[key] === ''
    })
    setFormErrors(errors)
    return Object.keys(errors).every((key) => errors[key] === false)
  }

  const cleanForm = () => {
    setFormState(initialValues)
    setFormErrors(initialErrors)
  }

  const handleSubmit = async () => {
    const data = cloneDeep(formState)
    if (data.currently_at_this_job) {
      data.end_of_contract = null
    } else {
      data.start_working_hours = null
      data.end_working_hours = null
    }
    
    if (handleValidate()) {
      setLoading(true)
      submitExtraAutoAnswer(token, questionData.uuid, formState)
        .then(() => {
          toast.success('Resposta enviada com sucesso.')
          cleanForm()
        })
        .catch(() => {
          toast.error('Erro ao enviar respostas, por favor tente novamente')
        })
        .finally(() => {
          setLoading(false)
        })
    } else {
      toast.error(
        'Há campos obrigatórios vazios. Verifique os campos preenchidos e tente novamente.',
      )
    }
  }

  const handleCleanAction = () => {
    setOpen(false)
    cleanForm()
  }

  const handleFieldChange = (event) => {
    const newFormState = cloneDeep(formState)
    const key = event.target.id
    newFormState[key] = event.target.value
    setFormState(newFormState)
  }

  const handleRadioChange = (event) => {
    const key = event.target.name
    const newFormState = cloneDeep(formState)
    newFormState[key] = event.target.value === 'yes'
    setFormState(newFormState)
  }
  
  const handleOpenConfirmCleaning = () => {
    setOpen(true)
  }
  
  const handleCloseConfirmCleaning = () => {
    setOpen(false)
  }

  return (
    <div className={classes.root}>
      <h2>Pessoa Exposta Politicamente</h2>
      <div className={classes.formWrapper}>
        <div className={classes.subject}>
          <p>
            <span>Motivo da ocorrência: </span>
            {questionData.occurrence.reason}
          </p>
        </div>
        <TextField
          id="role"
          label="Cargo:"
          onChange={handleFieldChange}
          fullWidth
          value={formState.role}
          error={formErrors.role}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          id="developed_activities"
          label="Atividades desempenhadas:"
          onChange={handleFieldChange}
          fullWidth
          value={formState.developed_activities}
          error={formErrors.developed_activities}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          id="reports_to"
          label="A quem se reporta(va) na estrutura do orgão:"
          onChange={handleFieldChange}
          fullWidth
          value={formState.reports_to}
          error={formErrors.reports_to}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          id="organization"
          label="A qual órgão é ou era vinculado?"
          onChange={handleFieldChange}
          fullWidth
          value={formState.organization}
          error={formErrors.organization}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <FormControl>
          <FormLabel>Ainda se encontra no exercicio da função?</FormLabel>
          <RadioGroup
            row
            value={formState.currently_at_this_job ? 'yes' : 'no'}
            name="currently_at_this_job"
            onChange={handleRadioChange}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Sim" />
            <FormControlLabel value="no" control={<Radio />} label="Não" />
          </RadioGroup>
        </FormControl>
        {formState.currently_at_this_job ? (
          <React.Fragment>
            <TextField
              id="start_working_hours"
              label="Início da jornada de trabalho"
              onChange={handleFieldChange}
              type="time"
              fullWidth
              value={formState.start_working_hours}
              error={formErrors.start_working_hours}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              id="end_working_hours"
              label="Fim da jornada de trabalho"
              onChange={handleFieldChange}
              type="time"
              fullWidth
              value={formState.end_working_hours}
              error={formErrors.end_working_hours}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </React.Fragment>
        ) : (
          <TextField
            id="end_of_contract"
            label="Data exata do fim do vínculo"
            onChange={handleFieldChange}
            type="date"
            fullWidth
            value={formState.end_of_contract}
            error={formErrors.end_of_contract}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
        )}
        <div className={classes.buttonsContainer}>
          <Button onClick={handleOpenConfirmCleaning} variant="text">
            Limpar formulário
          </Button>
          <Button onClick={handleSubmit} loading={loading}>
            Responder
          </Button>
        </div>
      </div>
      <DialogConfirm 
        title='Tem certeza de que deseja limpar este formulário?'
        onConfirm={handleCleanAction}
        onCancel={handleCloseConfirmCleaning}
        open={open}
      >
        Esta ação é irreversível e não poderá ser desfeita.
      </DialogConfirm>
    </div>
  )
}

PepForm.propTypes = {
  questionData: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    text: PropTypes.string,
    occurrence: PropTypes.object,
    answered: PropTypes.bool.isRequired,
    subject: PropTypes.string.isRequired,
    answer: PropTypes.object,
  }).isRequired,
}

export default PepForm
